import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { patchApplicationForm, postApplicationForm } from '@/api/Rover/ApplicationForm';
import type { IPatchApplicationFormServicePayload, IPostApplicationFormServicePayload } from '@/api/Rover/ApplicationForm/types';
import type { IPostProjectAssetPayload } from '@/api/Sherpa/Project/modules/ProjectAsset/types';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactMultiSelect, HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import type { TApplicationFormType } from '@/features/ApplicationForm/types';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { useUsers } from '@/hooks/ReactQueryHooks/useUsersQuery';
import { useWorkspaces } from '@/hooks/ReactQueryHooks/useWorkspaceQuery';
import { notifyUser } from '@/store/Toast';
import type { OrgTitlesInterface } from '@/types/role';
import type { User } from '@/types/User';
import type { IWorkspace } from '@/types/Workspace';
import { sanitizeResponseError, sortAlphabetically } from '@/utils';
import useUpload from '../../../../hooks/useUpload';
import { ProgramSelect } from './ProgramSelect';

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Required')
    .min(2, 'Full Name must be greater than 2 characters')
    .max(150, 'Full Name must be less than 25 characters'),
  program: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).test('program', 'You must select a valid program', (val) => !!val.value),
  formType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).test('formType', 'You must select a valid form type.', (val) => !!val.value),
  titles: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string(),
        id: Yup.string(),
      })
    )
    .test('titles', 'You must select a valid viewers.', (val) => (val ? val.some((title) => title.id !== '') : false)),
  // leadById: Yup.object({
  //   label: Yup.string(),
  //   value: Yup.string(),
  // }).test('leadById', 'You must select a valid form lead.', (val) =>
  //   val.value ? true : false
  // ),
  description: Yup.string().max(250, 'max character limit is 250'),
  logo: Yup.mixed().nullable(),
});

export const applicationFormTypes = [
  { label: 'Client Application', value: 'CLIENT_APPLICATION' },
  { label: 'Partner Proposal', value: 'PARTNER_PROPOSAL' },
];

interface ITitles {
  id: string;
  name: string;
}
interface INewApplicationFormData {
  name: string;
  formType: { label: string; value: string };
  program: { label: string; value: string };
  // leadById?: { label: string; value: number };
  description?: string;
  logo: File[] | null;
  titles: ITitles[];
}

interface INewApplicationFormHookForm {
  handleModal: () => void;
}

const NewApplicationFormHookForm = ({ handleModal }: INewApplicationFormHookForm) => {
  const dispatch = useDispatch();
  const uploadApplicationLogo = useUpload();
  const queryClient = useQueryClient();
  const fetchUsers = useUsers({ params: { active: 'true' } });
  const users = fetchUsers.data?.data;

  const fetchWorkspaces = useWorkspaces({
    params: { archived: 'false' },
  });

  const workspaces: IWorkspace[] | undefined = [...(fetchWorkspaces?.data || [])]
    .filter((workspace) => workspace.name !== 'All Program')
    .sort((a, b) => sortAlphabetically(a.name, b.name));

  const { data: fetchOrgTitles } = useOrgTitles({
    params: { perPage: 200, page: 1 },
  });

  const authorizedProjectLeadRoles = ['SUPER ADMIN', 'ADMIN', 'PROJECT MANAGER'];

  const authorizedProjectLeadUsers: User[] = [];

  users?.forEach((user: User) => {
    if (authorizedProjectLeadRoles.includes(user.role.toUpperCase())) {
      authorizedProjectLeadUsers.push(user);
    }
  });

  const _validLeadOptions = authorizedProjectLeadUsers.map(({ id, name, email }) => ({
    label: `${name} <${email}>`,
    value: id,
  }));

  const updateApplicationForm = useMutation(
    ({ data, applicationId }: { data: Pick<IPatchApplicationFormServicePayload, 'logo'>; applicationId: string }) =>
      patchApplicationForm({ data, applicationFormId: applicationId }),
    {
      onSuccess: () => {},
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const addApplicationForm = useMutation(
    (payload: { data: IPostApplicationFormServicePayload; logo: File[] }) => postApplicationForm({ data: payload?.data }),
    {
      onSuccess: (res, payload) => {
        if (payload.logo) {
          uploadApplicationLogo.uploadFile({
            file: payload.logo[0],
            resource: 'applicationForm',
            resourceId: res?.data?.id,
            path: 'thumbnails',
            applyData: (signedFiles: any) => {
              const data: Omit<IPostProjectAssetPayload, 'phase'> = {
                name: payload.logo[0].name,
                key: signedFiles.key,
                contentType: signedFiles.type,
                assetType: 'image',
                size: signedFiles.size,
              };
              updateApplicationForm.mutate({
                data: { logo: data },
                applicationId: res?.data.id,
              });
            },
          });
        }

        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_FORMS]);
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${payload.data.name} application form created successfully.`,
            toastType: 'success',
          })
        );
      },
      onError: (error) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
        handleModal();
      },
    }
  );

  const onSubmit = (formData: INewApplicationFormData) => {
    const { logo, program, ...rest } = formData;
    const data: IPostApplicationFormServicePayload = {
      ...rest,
      formType: formData?.formType?.value as TApplicationFormType,
      workspaceId: Number(program.value),
      titles: formData?.titles?.map((d: ITitles) => Number(d?.id)),
    };

    addApplicationForm.mutate({ data: data, logo: logo! });
  };

  return (
    <HookForm onSubmit={onSubmit} className='space-y-4'>
      <ProgramSelect workspaces={workspaces} />
      <HookInput id='name' name='name' type='text' label='Name' />
      <HookReactSingleSelect
        id='form-type'
        options={applicationFormTypes}
        placeholder='Select Form Type'
        name='formType'
        label='Form Type'
      />
      <HookReactMultiSelect
        name='titles'
        placeholder='Select Viewers'
        required
        getOptionLabel={(option) => (option as OrgTitlesInterface).name}
        getOptionValue={(option) => (option as OrgTitlesInterface).id.toString()}
        label='Viewers'
        options={fetchOrgTitles?.data?.map((item) => ({
          id: item?.id,
          name: item?.name,
        }))}
      />
      <HookTextarea id='description' name='description' label='Description' />
      <HookFileInput id='logo' name='logo' label='Logo' />
      <div className='flex gap-2 justify-end mt-5'>
        <Button label='Cancel' btnStyle='neutral' onClick={handleModal} />
        <Button label='Submit' btnStyle='primary' btnType='submit' />
      </div>
    </HookForm>
  );
};

export const NewApplicationForm = (props: INewApplicationFormHookForm) => {
  return (
    <Modal>
      <h2 className='mt-0 capitalize'>New Application Form</h2>
      <HookFormProvider resolver={yupResolver(validationSchema as any)} mode='all'>
        <NewApplicationFormHookForm {...props} />
      </HookFormProvider>
    </Modal>
  );
};
